import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { projectEndpoints } from 'src/api/project'
import { usePreviewOlexMatch } from 'src/api/projectVersionPositions'
import { requestProvider } from 'src/api/requestProvider'
import { IPositionWithDrawing } from 'src/generated-types'
import { IOlexMatchPreview } from 'src/generated-types/olex-match'
import { IProjectInfo } from 'src/generated-types/project'
import { OlexRequest, olexFormData, usesProjectOlexUpload } from './olexMatch.action'
import { isDraftVersion } from './projectVersion.action'

// The draft's positions as they are now, which the project form takes over after an Olex file was applied to it.
export const fetchProjectPositions = async (project: IProjectInfo): Promise<IPositionWithDrawing[]> => {
  const { data } = await requestProvider.get<IProjectInfo>(
    projectEndpoints.getProjectForMap(project.customerId, project.localityId, project.id),
  )
  return data.positions ?? []
}

// In a draft, an Olex file laid over the existing grid goes through the revision upload, which keeps names: the file
// is handed to onMatched, which shows it in the Configure Positions dialog. Resolves false when the project's regular
// upload should take the file instead.
export const useRevisionOlexUpload = (project: IProjectInfo, onMatched: (request: OlexRequest) => void) => {
  const { t } = useTranslation('projects')
  const { mutateAsync: previewMatch } = usePreviewOlexMatch(project.customerId, project.localityId, project.id)
  const isRevision = isDraftVersion(project)

  return useCallback(
    async (file: File) => {
      if (!isRevision) return false
      let preview: IOlexMatchPreview
      try {
        preview = (await previewMatch(olexFormData(file))).data as unknown as IOlexMatchPreview
      } catch {
        toast.error(t('revision-olex-failed', { ns: 'projects' }))
        return true
      }
      if (usesProjectOlexUpload(preview.status)) return false
      onMatched({ file, preview })
      return true
    },
    [isRevision, previewMatch, t, onMatched],
  )
}
